/* Findings — the machines losing the most, named and explained, with the
 * stage table and the stoppage register beside them. */

import { Blueprint, PanelHead } from '../../components/ui'
import { num } from '../../lib/format'
import {
  bandFill, dayNum, Meter, PatternTag, R, Spark, story, unitName,
} from './parts'
import type { Ctx } from './parts'

/* The register folds some sixty typed reasons a month; past this many the
   bars are a few minutes each and say nothing, so they are added up. */
const STOP_ROWS = 12

export function FindingsTab({ c }: { c: Ctx }) {
  return (
    <>
      <Findings c={c} />
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(420px,1fr))', gap: 24,
      }}>
        <StageTable c={c} />
        <Stoppages c={c} />
      </div>
    </>
  )
}

function Findings({ c }: { c: Ctx }) {
  const { data } = c
  const byCode = new Map(data.machines.map((m) => [m.code, m]))
  const rows = data.findings.map((code) => byCode.get(code)!).filter(Boolean)
  const span = data.dates.length
    ? `${dayNum(data.dates[0])}–${dayNum(data.dates[data.dates.length - 1])} ${c.monShort}` : ''

  return (
    <Blueprint className="sheet">
      <PanelHead title="Where the output is going missing"
        hint={`Ranked by output lost against standard · benchmark ${c.benchLabel}`} />
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: 18 }}>
        {rows.map((m, i) => (
          <div key={m.code} style={{
            display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) 150px 132px', gap: 18,
            alignItems: 'start', padding: '16px 0', borderTop: '1px solid var(--color-divider)',
          }}>
            <div style={{
              fontFamily: 'var(--font-heading)', fontSize: 26, lineHeight: 1,
              color: 'var(--color-neutral-500)',
            }}>{String(i + 1).padStart(2, '0')}</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => c.open(m.code)} style={{
                  font: 'inherit', fontFamily: 'var(--font-heading)', fontSize: 19,
                  background: 'none', border: 0, padding: 0, color: 'var(--color-text)',
                  cursor: 'pointer', textAlign: 'left',
                }}>{m.full_name}</button>
                <PatternTag pattern={m.pattern} />
                <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                  {m.n} days on record · std {num(m.std)} kg/shift
                </span>
              </div>
              <div className="pretty" style={{
                fontSize: 14, lineHeight: 1.5, color: 'var(--color-neutral-800)',
                marginTop: 6, maxWidth: '78ch',
              }}>{story(m, c)}</div>
            </div>
            <div>
              <Spark m={m} dates={data.dates} bench={c.bench} height={30} pixels={34} />
              <div style={{ fontSize: 10, color: 'var(--color-neutral-600)', marginTop: 2 }}>
                Daily efficiency, {span}
              </div>
            </div>
            <div className="num" style={R}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 24, lineHeight: 1 }}>
                {num(m.mean, 1)}%
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                avg day eff · ±{num(m.sd, 1)}
              </div>
              <div style={{ fontSize: 13, marginTop: 6 }}>{num(m.lost)} kg</div>
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>lost vs standard</div>
            </div>
          </div>
        ))}
        {!rows.length ? (
          <div style={{
            fontSize: 13, color: 'var(--color-neutral-700)', padding: '16px 0',
            borderTop: '1px solid var(--color-divider)',
          }}>
            Every machine in this selection is on standard.
          </div>
        ) : null}
      </div>
    </Blueprint>
  )
}

function StageTable({ c }: { c: Ctx }) {
  return (
    <Blueprint className="sheet">
      <PanelHead title="Stage by stage" />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
        Actual against standard across the month. Stage kilos are not comparable between stages.
      </div>
      <table className="table" style={{ marginTop: 16 }}>
        <thead>
          <tr>
            <th>Stage</th><th style={R}>Mcs</th><th>Utilisation</th>
            <th style={R}>Lost kg</th><th>Weakest machine</th>
          </tr>
        </thead>
        <tbody>
          {c.data.stage_rows.map((r) => (
            <tr key={`${r.unit}-${r.stage}`}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.stage}
                <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{unitName(r.unit)}</div>
              </td>
              <td className="num" style={R}>{r.machines}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Meter width={80} value={r.efficiency ?? 0} max={125}
                    fill={bandFill(r.efficiency ?? 0, c.bench)} />
                  <span className="num" style={{ fontSize: 13 }}>{num(r.efficiency, 1)}%</span>
                </div>
              </td>
              <td className="num" style={R}>{num(r.lost)}</td>
              <td style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>
                {r.weakest.name} at {num(r.weakest.mean, 1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Blueprint>
  )
}

function Stoppages({ c }: { c: Ctx }) {
  const { data } = c
  const all = data.stoppages.rows.filter((r) => r.min > 0)
  const total = data.stoppages.total
  const shown = all.slice(0, STOP_ROWS)
  const rest = all.slice(STOP_ROWS)
  const restMin = rest.reduce((a, r) => a + r.min, 0)
  const max = shown.length ? shown[0].min : 1

  const scope = [
    data.scope.unit ? unitName(data.scope.unit).toLowerCase() : 'both units',
    data.scope.stage ? data.scope.stage : null,
  ].filter(Boolean).join(', ')

  const top3 = all.slice(0, 3)
  const note = !data.register
    ? `No stoppage register is on file for ${c.monLong} — the floor reports for the month carried output only.`
    : !top3.length
      ? 'No stoppage minutes were booked for this selection.'
      : `${listed(top3.map((r) => r.reason))} account${top3.length === 1 ? 's' : ''} for ${num(top3.reduce((a, r) => a + r.share, 0))}% of all booked stoppage time. The register is booked against each machine and shift, so it can point at a machine as well as explain the erratic ones — open any machine to see what is booked against it.`

  const row = (label: string, min: number, width: number, share: number) => (
    <div key={label} style={{
      display: 'grid', gridTemplateColumns: '210px minmax(0,1fr) 96px',
      alignItems: 'center', gap: 14,
    }}>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-800)', lineHeight: 1.2 }}>{label}</div>
      <svg viewBox="0 0 300 14" preserveAspectRatio="none"
        style={{ width: '100%', height: 14, display: 'block' }}>
        <rect x="0" y="0" width="300" height="14" fill="var(--c-track)" />
        <rect x="0" y="0" width={Math.max(1, Math.min(300, width))} height="14" fill="var(--c-bar1)" />
      </svg>
      <div className="num" style={{ fontSize: 13, textAlign: 'right' }}>
        {num(min)}<span style={{ color: 'var(--color-neutral-500)' }}> · {num(share)}%</span>
      </div>
    </div>
  )

  return (
    <Blueprint className="sheet">
      <PanelHead title="Recorded stoppage time" />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
        From the daily stoppage register, {scope} · {num(total)} minutes booked
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 18 }}>
        {shown.map((r) => row(r.reason, r.min, (r.min / max) * 300, r.share))}
        {rest.length
          ? row(`${rest.length} other reasons`, restMin, (restMin / max) * 300,
            total ? (restMin / total) * 100 : 0)
          : null}
      </div>
      <div className="pretty" style={{
        fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 18, lineHeight: 1.5,
      }}>{note}</div>
    </Blueprint>
  )
}

/** 'A, B and C' */
function listed(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
